<template>
  <div class="bg-white rounded-xl shadow-lg p-6">
    <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
      <span>⏱️</span>
      Métricas de Tiempo
    </h2>

    <!-- Evaluación General -->
    <div :class="getEstadoClass(metricas.evaluacion.estado)" class="rounded-lg p-4 mb-6">
      <div class="flex items-center gap-3">
        <span class="text-3xl">{{ getEstadoIcono(metricas.evaluacion.estado) }}</span>
        <div>
          <p class="font-semibold text-gray-900">{{ metricas.evaluacion.estado }}</p>
          <p class="text-sm text-gray-700">{{ metricas.evaluacion.mensaje }}</p>
          <p class="text-xs text-gray-600 mt-1">Promedio general: {{ metricas.evaluacion.promedio_general }} días</p>
        </div>
      </div>
    </div>

    <!-- Tiempos Promedio -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p class="text-sm text-gray-600 mb-1">Venta → Producción</p>
        <p class="text-3xl font-bold text-blue-600">{{ metricas.tiempos_promedio.venta_a_produccion }}</p>
        <p class="text-xs text-gray-500">días promedio</p>
      </div>
      <div class="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <p class="text-sm text-gray-600 mb-1">Producción → Instalación</p>
        <p class="text-3xl font-bold text-purple-600">{{ metricas.tiempos_promedio.produccion_a_instalacion }}</p>
        <p class="text-xs text-gray-500">días promedio</p>
      </div>
      <div class="bg-green-50 rounded-lg p-4 border border-green-200">
        <p class="text-sm text-gray-600 mb-1">Ciclo Completo</p>
        <p class="text-3xl font-bold text-green-600">{{ metricas.tiempos_promedio.ciclo_completo }}</p>
        <p class="text-xs text-gray-500">días promedio</p>
      </div>
    </div>

    <!-- Rangos Min/Max -->
    <div class="grid grid-cols-2 gap-4 mb-6">
      <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
        <h3 class="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <span>⚡</span>
          Tiempos Mínimos
        </h3>
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Venta → Producción:</span>
            <span class="font-bold text-green-700">{{ metricas.tiempos_minimos.venta_a_produccion }} días</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Producción → Instalación:</span>
            <span class="font-bold text-green-700">{{ metricas.tiempos_minimos.produccion_a_instalacion }} días</span>
          </div>
        </div>
      </div>
      
      <div class="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
        <h3 class="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <span>🐌</span>
          Tiempos Máximos
        </h3>
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Venta → Producción:</span>
            <span class="font-bold text-red-700">{{ metricas.tiempos_maximos.venta_a_produccion }} días</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Producción → Instalación:</span>
            <span class="font-bold text-red-700">{{ metricas.tiempos_maximos.produccion_a_instalacion }} días</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Distribución de Tiempos -->
    <div>
      <h3 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <span>📊</span>
        Distribución de Tiempos
      </h3>
      <div class="space-y-2">
        <div
          v-for="(dist, index) in metricas.distribucion_tiempos"
          :key="index"
          class="flex items-center gap-3"
        >
          <span class="text-sm text-gray-600 w-24">{{ dist.rango }}</span>
          <div class="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
            <div 
              class="bg-gradient-to-r from-blue-400 to-blue-600 h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
              :style="`width: ${getPercentage(dist.cantidad)}%`"
            >
              <span class="text-white text-xs font-semibold">{{ dist.cantidad }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MetricasTiempo',
  props: {
    metricas: {
      type: Object,
      required: true
    }
  },
  methods: {
    getEstadoClass(estado) {
      switch (estado) {
        case 'EXCELENTE':
          return 'bg-green-100 border-2 border-green-300';
        case 'BUENO':
          return 'bg-blue-100 border-2 border-blue-300';
        case 'REGULAR':
          return 'bg-yellow-100 border-2 border-yellow-300';
        case 'DEFICIENTE':
          return 'bg-red-100 border-2 border-red-300';
        default:
          return 'bg-gray-100 border-2 border-gray-300';
      }
    },
    getEstadoIcono(estado) {
      switch (estado) {
        case 'EXCELENTE':
          return '🌟';
        case 'BUENO':
          return '👍';
        case 'REGULAR':
          return '⚠️';
        case 'DEFICIENTE':
          return '🔴';
        default:
          return '⚪';
      }
    },
    getPercentage(cantidad) {
      if (!this.metricas.distribucion_tiempos) return 0;
      const max = Math.max(...this.metricas.distribucion_tiempos.map(d => d.cantidad));
      return (cantidad / max) * 100;
    }
  }
};
</script>
